import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import {
  PROGRAMMES_STUDIO_FEATURE_CODE,
  programmesComparer,
} from '../../services/programmes';
import EditPlanModal from './edit_plan_modal';
import EditProgrammeModal from './edit_programme_modal';
import NewProgrammeModal from './new_programme_modal';
import NewProgrammePage from './new_programme_page';
import ProgrammesRequestAccessPage from './programmes_request_access';
import ProgrammeView from './programme_view';
import TopNavBarMobileProgrammes from './top_nav_bar_mobile_programmes';
import EditNutritionConstraintsContainer from './edit_nutrition_constraints_container';
import ProgrammePlanPreview from './programme_plan_preview';
import {
  appSyncGraphQLApi,
  useGetProgrammesQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import ResourceStatusControl from '../common/resource_status_control';
import StaleScreenModal from '../common/stale_screen_modal';
import {
  areResourcesStaleSelector,
  StaleResourceType,
} from '../../features/stale_resource_detection/stale_resource_detection_slice';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { currentProgrammeAndPlanChanged } from '../../features/smorg_studio_programmes/smorg_studio_programmes_slice';
import { SmorgRootState } from '../../app/smorg_store';

const ProgrammesView = () => {
  const dispatch = useDispatch();

  const { data: userProfile } = useGetUserProfileQuery();

  const navigate = useNavigate();

  const { data: ownedProgrammesUnsorted, isLoading: isOwnedProgrammesLoading } =
    useGetProgrammesQuery(undefined);

  const ownedProgrammes = useMemo(() => {
    if (!ownedProgrammesUnsorted) {
      return [];
    }
    const ownedProgrammesSorted = [...ownedProgrammesUnsorted];
    ownedProgrammesSorted.sort(programmesComparer);
    return ownedProgrammesSorted;
  }, [ownedProgrammesUnsorted]);

  const userHasAccess = (userProfile?.enabledFeatures || []).includes(
    PROGRAMMES_STUDIO_FEATURE_CODE,
  );

  const { programmeId: routeProgrammeId, planId: routePlanId } = useParams();
  const lastUsedProgrammeId = useSelector(
    (state: SmorgRootState) => state.smorgStudioProgrammes.currentProgrammeId,
  );
  const actualProgrammeId =
    routeProgrammeId || lastUsedProgrammeId || ownedProgrammes[0]?.id;
  const programme = ownedProgrammes.find((pr) => pr.id === actualProgrammeId);

  const lastUsedPlanId = useSelector(
    (state: SmorgRootState) => state.smorgStudioProgrammes.currentPlanId,
  );

  let actualPlanId = routePlanId || lastUsedPlanId;
  if (
    !actualPlanId ||
    programme?.plans.findIndex((pl) => pl.id === actualPlanId) === -1
  ) {
    actualPlanId = programme?.plans[0].id || ownedProgrammes[0]?.plans[0].id;
  }

  const isRouteCreateProgramme = useMatch('/programmes/create');

  const isRouteEditProgramme = useMatch('/programmes/:programmeId/edit');

  const isRouteEditNutritionConstraintsProgramme = useMatch(
    '/programmes/:programmeId/nutritionConstraints/edit',
  );

  const isRouteEditPlan = useMatch(
    '/programmes/:programmeId/plans/:planId/edit',
  );

  const isRoutePreviewPlan = useMatch(
    '/programmes/:programmeId/plans/:planId/preview',
  );

  // const showDeepDuplicate = !!useMatch(
  //   '/programmes/:programmeId/deepDuplicate',
  // );

  const showNewProgrammeModal =
    (ownedProgrammes.length === 0 && !isOwnedProgrammesLoading) ||
    isRouteCreateProgramme;

  // console.log({ isRouteCreateProgramme, isRouteEditProgramme, isRouteEditPlan, showNewProgrammePage });

  // useEffect(() => {
  //   if (!userHasSeenIntro) {

  //   }
  // }, [userHasSeenIntro])

  useEffect(() => {
    if (
      actualProgrammeId !== lastUsedProgrammeId ||
      actualPlanId !== lastUsedPlanId
    ) {
      dispatch(
        currentProgrammeAndPlanChanged({
          programmeId: actualProgrammeId,
          planId: actualPlanId,
        }),
      );
    }
  }, [
    actualProgrammeId,
    actualPlanId,
    lastUsedProgrammeId,
    lastUsedPlanId,
    dispatch,
  ]);

  const isBoardViewStale = useSelector((state: SmorgRootState) =>
    areResourcesStaleSelector(state, 'Programme', [actualProgrammeId]),
  );

  const onReloadStaleData = () => {
    dispatch(appSyncGraphQLApi.util.invalidateTags(['Programme']));
  };

  if (!userHasAccess) {
    return <ProgrammesRequestAccessPage />;
  }

  if (ownedProgrammes.length === 0 && !isOwnedProgrammesLoading) {
    return <NewProgrammePage />;
  }

  return (
    <>
      <TopNavBarMobileProgrammes
        selectedProgrammeId={actualProgrammeId}
        selectedPlanId={actualPlanId}
      />
      {!isBoardViewStale && isRoutePreviewPlan ? (
        <ProgrammePlanPreview
          programmeId={actualProgrammeId}
          planId={actualPlanId}
          // showDeepDuplicate={showDeepDuplicate}
        />
      ) : (
        <ProgrammeView
          programmeId={actualProgrammeId}
          planId={actualPlanId}
          // showDeepDuplicate={showDeepDuplicate}
        />
      )}
      {!isBoardViewStale && showNewProgrammeModal && <NewProgrammeModal />}
      {!isBoardViewStale && isRouteEditProgramme && (
        <EditProgrammeModal programmeId={actualProgrammeId} />
      )}
      {!isBoardViewStale && isRouteEditNutritionConstraintsProgramme && (
        <EditNutritionConstraintsContainer
          programmeId={actualProgrammeId}
          onDismiss={() => navigate(-1)}
        />
      )}
      {!isBoardViewStale && isRouteEditPlan && (
        <EditPlanModal programmeId={actualProgrammeId} planId={actualPlanId} />
      )}
      {isBoardViewStale && <StaleScreenModal onSync={onReloadStaleData} />}
      <ResourceStatusControl
        resourceType={StaleResourceType.Programme}
        resourceIDs={[actualProgrammeId]}
      />
    </>
  );
};

export default ProgrammesView;
